import { useState } from "react";
import { Search, Printer, Loader2, Download, Users, FileText, Paperclip } from "lucide-react";
import { downloadFile } from "@/portal/api";
import { sanitizeName, CAT_LABEL } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";
import CooperationSection from "@/portal/components/CooperationSection";

const DocumentsTab = ({ clients, token }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const grab = async (path, filename, key) => {
    setBusy(key);
    try {
      await downloadFile(path, token, filename);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const needle = q.trim().toLowerCase();
  const matchStatus = (ct) =>
    statusFilter === "all" || (statusFilter === "signed" ? ct.status === "signed" : ct.status !== "signed");
  const rows = clients
    .map((c) => ({
      ...c,
      contracts: (c.contracts || []).filter(matchStatus),
    }))
    .filter((c) => c.contracts.length > 0)
    .filter((c) => {
      if (!needle) return true;
      return (c.name || "").toLowerCase().includes(needle)
        || c.contracts.some((ct) => (ct.car_label || "").toLowerCase().includes(needle));
    });
  const totalContracts = clients.reduce((s, c) => s + (c.contracts || []).length, 0);
  const signedCount = clients.reduce((s, c) => s + (c.contracts || []).filter((ct) => ct.status === "signed").length, 0);

  return (
    <div data-testid="documents-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-['Cabinet_Grotesk'] text-xl font-bold">Dokumenty ke stažení</h2>
          <p className="text-sm text-zinc-500">Všechny smlouvy a předávací protokoly na jednom místě ({totalContracts} smluv celkem).</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat klienta nebo vůz…"
            className="input-dark pl-9 sm:w-72"
            data-testid="documents-search"
          />
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mb-4 border-indigo-500/20" data-testid="documents-templates">
        <div className="flex items-center gap-2 mb-1">
          <Printer className="w-4 h-4 text-indigo-400" />
          <span className="font-semibold">Prázdné šablony k tisku</span>
        </div>
        <p className="text-xs text-zinc-500 mb-3">Stáhněte a vytiskněte nevyplněnou smlouvu nebo předávací protokol k ručnímu vyplnění.</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => grab(`/admin/templates/contract/pdf`, "smlouva-effectdeal-prazdna.pdf", "tpl-contract")}
            disabled={busy === "tpl-contract"}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-60"
            data-testid="documents-template-contract"
          >
            {busy === "tpl-contract" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Prázdná smlouva (PDF)
          </button>
          <button
            onClick={() => grab(`/admin/templates/protocol/pdf`, "protokol-effectdeal-prazdny.pdf", "tpl-protocol")}
            disabled={busy === "tpl-protocol"}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 disabled:opacity-60"
            data-testid="documents-template-protocol"
          >
            {busy === "tpl-protocol" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Prázdný protokol (PDF)
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="documents-filters">
        {[["all", `Vše (${totalContracts})`], ["signed", `Podepsané (${signedCount})`], ["pending", `Čeká na podpis (${totalContracts - signedCount})`]].map(([val, lbl]) => (
          <button key={val} onClick={() => setStatusFilter(val)}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${statusFilter === val ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white border border-white/10"}`}
            data-testid={`documents-filter-${val}`}>{lbl}</button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500 py-10 text-center" data-testid="documents-empty">Žádné smlouvy k zobrazení.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((client) => (
            <div key={client.id} className="glass rounded-2xl p-5" data-testid={`documents-client-${client.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-sky-400 shrink-0" />
                <span className="font-semibold">{client.name}</span>
                <span className="text-xs text-zinc-500 break-all">{client.email}</span>
              </div>
              <div className="space-y-3">
                {(client.contracts || []).map((ct) => {
                  const signed = ct.status === "signed";
                  const cFile = `smlouva-${sanitizeName(client.name)}-${sanitizeName(ct.car_label)}.pdf`;
                  return (
                    <div key={ct.id} className="rounded-xl bg-white/5 p-3" data-testid={`documents-contract-${ct.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-indigo-400" />
                          <span className="text-zinc-200">{ct.car_label || "Smlouva"}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${signed ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                            {signed ? "Podepsáno" : "Čeká na podpis"}
                          </span>
                        </div>
                        <button
                          onClick={() => grab(`/admin/contracts/${ct.id}/pdf`, cFile, `c-${ct.id}`)}
                          disabled={busy === `c-${ct.id}`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-60"
                          data-testid={`documents-download-contract-${ct.id}`}
                        >
                          {busy === `c-${ct.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                          Stáhnout smlouvu
                        </button>
                      </div>
                      {(ct.protocols || []).length > 0 && (
                        <div className="mt-2.5 pl-6 space-y-1.5">
                          {ct.protocols.map((p) => {
                            const pFile = p.original_filename || `protokol-${p.category || "protokol"}-${(p.id || "").slice(0, 8)}.pdf`;
                            return (
                              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={`documents-protocol-${p.id}`}>
                                <span className="text-zinc-400 inline-flex items-center gap-1.5">
                                  <Paperclip className="w-3 h-3" /> {CAT_LABEL[p.category] || "Protokol"}
                                  {p.created_at ? ` · ${new Date(p.created_at).toLocaleDateString("cs-CZ")}` : ""}
                                </span>
                                <button
                                  onClick={() => grab(`/admin/protocols/${p.id}/file`, pFile, `p-${p.id}`)}
                                  disabled={busy === `p-${p.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 disabled:opacity-60"
                                  data-testid={`documents-download-protocol-${p.id}`}
                                >
                                  {busy === `p-${p.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                  Stáhnout protokol
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <CooperationSection token={token} />
    </div>
  );
};

export default DocumentsTab;
